<script setup>
import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import MediaLibraryPanel from 'dashboard/routes/dashboard/marketing/pages/MediaLibraryPanel.vue';
import { imagePx } from '../../canvas/canvasStyles';
import ColorField from '../../fields/ColorField.vue';
import SpacingField from '../../fields/SpacingField.vue';
import SliderField from '../../fields/SliderField.vue';
import AlignField from '../../fields/AlignField.vue';
import VisibilityField from '../../fields/VisibilityField.vue';
import { withScheme } from '../../document/links';

const props = defineProps({
  node: { type: Object, required: true },
  // Elemento do rodapé fica sempre visível (o servidor força): sem o campo.
  selection: { type: Object, default: null },
});
const emit = defineEmits(['update']);
const ed = inject('emailEditor');
const { t } = useI18n();
// Ao sair do campo, domínio sem esquema ganha https:// (sem mexer enquanto digita).
const onLinkBlur = value => {
  const fixed = withScheme(value);
  if (fixed !== value) emit('update', { link: fixed }, 'link');
};
const media = ref(null);
const theme = computed(() => ed.template.value?.theme || {});
const st = computed(() => props.node.style || {});
const width = computed(() => props.node.width || { value: 100, unit: '%' });
// GIF animado: o Outlook do Windows mostra só o primeiro quadro.
const isGif = computed(() =>
  /\.gif(?:$|[?#])/i.test(props.node.media?.url || '')
);
const style = (key, value) => emit('update', { style: { [key]: value } }, key);
const pick = asset => {
  emit('update', { media: { asset_id: asset.id, url: asset.url } }, 'media');
  media.value?.close();
};
const setUnit = unit => {
  if (unit === width.value.unit) return;
  const value = unit === 'px' ? imagePx(props.node, 520) : 100;
  emit('update', { width: { value, unit } }, 'width');
};
</script>

<template>
  <div data-props="image" class="flex flex-col gap-4">
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white"
        @click="media?.open()"
      >
        {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.CHOOSE_IMAGE') }}
      </button>
      <button
        v-if="node.media?.url"
        type="button"
        class="text-xs text-n-ruby-11"
        @click="
          emit('update', { media: { asset_id: null, url: null } }, 'media')
        "
      >
        {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.REMOVE_IMAGE') }}
      </button>
    </div>
    <p v-if="node.type === 'logo'" class="text-[11px] text-n-slate-10">
      {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.LOGO_HINT') }}
    </p>
    <p v-if="isGif" data-test="gif-warning" class="text-[11px] text-n-amber-11">
      {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.GIF_OUTLOOK') }}
    </p>
    <div class="flex flex-col gap-1.5">
      <div
        class="flex gap-0.5 self-start rounded-lg border border-n-weak p-0.5"
      >
        <button
          type="button"
          class="rounded-md px-2 py-0.5 text-xs"
          :class="
            width.unit === 'px'
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-11'
          "
          @click="setUnit('px')"
        >
          {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.IMAGE_PX') }}
        </button>
        <button
          type="button"
          class="rounded-md px-2 py-0.5 text-xs"
          :class="
            width.unit === '%'
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-11'
          "
          @click="setUnit('%')"
        >
          {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.IMAGE_PCT') }}
        </button>
      </div>
      <SliderField
        :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.IMAGE_WIDTH')"
        :model-value="width.value"
        :min="width.unit === 'px' ? 20 : 5"
        :max="width.unit === 'px' ? 800 : 100"
        :unit="
          width.unit === 'px'
            ? t('EMAIL_TEMPLATES.EDITOR.PROPS.UNIT_PX')
            : t('EMAIL_TEMPLATES.EDITOR.PROPS.UNIT_PCT')
        "
        @update:model-value="
          v =>
            emit('update', { width: { value: v, unit: width.unit } }, 'width')
        "
      />
    </div>
    <AlignField
      :model-value="node.align"
      @update:model-value="v => emit('update', { align: v }, 'align')"
    />
    <label data-field="link" class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('EMAIL_TEMPLATES.EDITOR.PROPS.LINK')
      }}</span>
      <input
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-sm"
        :value="node.link"
        :placeholder="t('EMAIL_TEMPLATES.EDITOR.TEXT.LINK_PROMPT')"
        @input="emit('update', { link: $event.target.value }, 'link')"
        @blur="onLinkBlur($event.target.value)"
      />
    </label>
    <label class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('EMAIL_TEMPLATES.EDITOR.PROPS.ALT')
      }}</span>
      <input
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-sm"
        :value="node.alt"
        @input="emit('update', { alt: $event.target.value }, 'alt')"
      />
    </label>
    <SliderField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.RADIUS')"
      :model-value="st.radius || 0"
      :min="0"
      :max="60"
      unit="px"
      @update:model-value="v => style('radius', v)"
    />
    <SliderField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.BORDER_WIDTH')"
      :model-value="st.border?.width || 0"
      :min="0"
      :max="10"
      unit="px"
      @update:model-value="
        v =>
          style(
            'border',
            v
              ? { width: v, color: st.border?.color || '#e6e9ee', sides: 'all' }
              : null
          )
      "
    />
    <ColorField
      v-if="st.border?.width"
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.BORDER_COLOR')"
      :theme="theme"
      :model-value="st.border.color"
      @update:model-value="v => style('border', { ...st.border, color: v })"
    />
    <SpacingField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.PADDING')"
      :model-value="st.padding"
      @update:model-value="v => style('padding', v)"
    />
    <VisibilityField
      v-if="selection?.row?.role !== 'footer'"
      :model-value="st.hide_on"
      @update:model-value="v => style('hide_on', v)"
    />
    <Dialog
      ref="media"
      :title="t('EMAIL_TEMPLATES.EDITOR.PROPS.CHOOSE_IMAGE')"
      :show-confirm-button="false"
      width="3xl"
      overflow-y-auto
    >
      <MediaLibraryPanel pickable @pick="pick" />
    </Dialog>
  </div>
</template>
